import React, { useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import {
  FaList, FaClock, FaUser, FaHourglassHalf, FaTimes,
  FaPlus, FaEdit, FaPhone, FaUsers, FaSave,
  FaBell, FaChair, FaCalendarAlt, FaChevronDown, FaChevronUp,
  FaHistory, FaUsers as FaUsersIcon, FaWhatsapp
} from 'react-icons/fa';
import { IP } from "../configuracion.js";
import { useProperty } from "../context/PropertyContext";
import { isOperationalToday, isUpcomingReservation, isConcludedOrExpired } from "../utils/listaEsperaHelpers";
// Requerimiento "WhatsApp para Reservaciones y Lista de Espera": modal
// independiente que reutiliza el mecanismo de envío ya existente (ver
// src/utils/reservationWhatsapp.js) — no cambia nada del flujo de
// reservaciones/lista de espera en sí, solo agrega el botón de acceso.
import ReservationWhatsAppModal from "./ReservationWhatsAppModal";
import "../styles/VentanaListaEspera.css";

const VentanaListaEspera = ({ waitingList, onClose, onUpdate, onAssign, revenues, assignMode = false, assignTargetName = null, canAssignToTable = false }) => {
  const { property } = useProperty();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showStatusMenu, setShowStatusMenu] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  // Requerimiento "WhatsApp": item sobre el que se abrió el modal de envío
  // (null = cerrado). Independiente de editingItem/showStatusMenu para no
  // interferir con esos flujos.
  const [whatsappItem, setWhatsappItem] = useState(null);
  // 'today' (operativo: walk-ins + reservas cuya fecha ya llegó),
  // 'reservations' (reservaciones futuras, todavía no operativas) o
  // 'history'. Antes solo existía 'active' mezclando reservaciones y
  // espera de hoy en una sola lista — eso causaba confusión sobre qué
  // clientes tienen reservación confirmada vs cuáles siguen en espera.
  const [activeTab, setActiveTab] = useState('today');
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '',
    party_size: '2',
    queue_type: '3',
    priority_level: '1',
    notes: '',
    scheduled_time: ''
  });

  console.log('🎯 [1] VentanaListaEspera montada', { property, waitingListLength: waitingList.length });

  // Catálogo de estados COMPLETO (para mostrar en badges)
  const fullStatusCatalog = [
    { id: 1, name: 'Reserva', color: '#3b82f6', icon: '📅' },
    { id: 2, name: 'Llegó', color: '#f59e0b', icon: '👋' },
    { id: 3, name: 'Espera', color: '#f59e0b', icon: '⏳' },
    { id: 4, name: 'Prioridad', color: '#ef4444', icon: '⭐' },
    { id: 5, name: 'Sentado', color: '#10b981', icon: '🪑' },
    { id: 6, name: 'Cancelado', color: '#6b7280', icon: '❌' },
    { id: 7, name: 'No Show', color: '#6b7280', icon: '🚫' }
  ];

  // Catálogo para el menú de estados (EXCLUYE Sentado ID 5)
  const statusMenuCatalog = fullStatusCatalog.filter(status => status.id !== 5);

  const formatMinutes = (minutes) => {
    if (!minutes || minutes === 0) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins} min`;
  };

  const getStatusInfo = (statusId) => {
    return fullStatusCatalog.find(s => s.id === statusId) || fullStatusCatalog[2];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      phone: '',
      party_size: '2',
      queue_type: '3',
      priority_level: '1',
      notes: '',
      scheduled_time: ''
    });
    setEditingItem(null);
    setShowAddForm(false);
  };

  const handleEdit = (item) => {
    console.log('✏️ [handleEdit] Editando item:', item);
    setEditingItem(item);
    setFormData({
      customer_name: item.customer_name || '',
      phone: item.phone || '',
      party_size: item.party_size?.toString() || '2',
      queue_type: item.queue_type?.toString() || '3',
      priority_level: item.priority_level?.toString() || '1',
      notes: item.notes || '',
      scheduled_time: item.scheduled_time || ''
    });
    setShowAddForm(true);
  };

  const handleSave = async () => {
    if (!formData.customer_name.trim()) {
      alert('El nombre del cliente es requerido');
      return;
    }

    try {
      const url = `${IP}/tavpos/backend/lista_espera_manage.php?property=${encodeURIComponent(property)}`;
      console.log('💾 [handleSave] Guardando:', { url, action: editingItem ? 'update' : 'create', data: formData });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: editingItem ? 'update' : 'create',
          data: {
            ...formData,
            queue_id: editingItem?.queue_id,
            party_size: parseInt(formData.party_size),
            priority_level: parseInt(formData.priority_level),
            queue_type: parseInt(formData.queue_type)
          }
        })
      });

      const result = await response.json();
      console.log('💾 [handleSave] Resultado:', result);
      
      if (result.success) {
        resetForm();
        if (onUpdate) onUpdate();
      } else {
        alert('Error al guardar: ' + result.error);
      }
    } catch (err) {
      console.error('💾 [handleSave] Error:', err);
      alert('Error al guardar');
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    console.log('🔵 [handleStatusChange] INICIO - Item:', item, 'NewStatus:', newStatus);
    
    try {
      const url = `${IP}/tavpos/backend/lista_espera_manage.php?property=${encodeURIComponent(property)}`;
      console.log('🔵 [handleStatusChange] URL:', url);
      
      const body = {
        action: 'change_status',
        queue_id: item.queue_id,
        new_status: newStatus
      };
      console.log('🔵 [handleStatusChange] Body:', body);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      console.log('🔵 [handleStatusChange] Response status:', response.status);
      
      const result = await response.json();
      console.log('🔵 [handleStatusChange] Resultado:', result);
      
      if (result.success) {
        console.log('🔵 [handleStatusChange] ✅ Éxito');
        setShowStatusMenu(null);
        
        if (onUpdate) {
          console.log('🔵 [handleStatusChange] Llamando onUpdate');
          await onUpdate();
        }
        
        console.log('🔵 [handleStatusChange] Estado actualizado');
        
        // Si el nuevo estado es 5, 6 o 7, cambiar a la pestaña de historial
        if ([5, 6, 7].includes(newStatus)) {
          setActiveTab('history');
          fetchHistory(); // Recargar historial
        }
        
      } else {
        console.error('🔵 [handleStatusChange] ❌ Error:', result.error);
        alert('Error al cambiar estado: ' + (result.error || 'Error desconocido'));
      }
    } catch (err) {
      console.error('🔵 [handleStatusChange] ❌ Error fetch:', err);
      alert('Error al cambiar estado: ' + err.message);
    }
    console.log('🔵 [handleStatusChange] FIN');
  };

  const handleAssignToTable = (item) => {
    console.log('🪑 [handleAssignToTable] Asignar a mesa:', item);
    // Cambiar estado a Sentado (5) automáticamente
    handleStatusChange(item, 5);
    // También llamar a onAssign si existe
    if (onAssign) {
      onAssign(item);
    }
    setShowStatusMenu(null);
    // CERRAR EL MODAL SOLO AQUÍ
    onClose();
  };

  const toggleExpand = (itemId) => {
    console.log('🔽 [toggleExpand] Item:', itemId, 'Nuevo estado:', !expandedItems[itemId]);
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleExpandClick = (itemId) => {
    console.log('👉 [handleExpandClick] Click en expandir para item:', itemId);
    toggleExpand(itemId);
  };

  const handleStatusMenuClick = (itemId) => {
    console.log('👉 [handleStatusMenuClick] Click en menú de estados para item:', itemId);
    console.log('Estado actual showStatusMenu:', showStatusMenu);
    setShowStatusMenu(showStatusMenu === itemId ? null : itemId);
    console.log('Nuevo estado showStatusMenu:', showStatusMenu === itemId ? null : itemId);
  };

  const handleStatusOptionClick = (item, status) => {
    console.log('👉 [handleStatusOptionClick] Click en opción de estado:', status);
    console.log('Item:', item);
    handleStatusChange(item, status.id);
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const url = `${IP}/tavpos/backend/lista_espera.php?property=${encodeURIComponent(property)}&filter=history`;
      console.log('📜 Fetching history from:', url);
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        console.log('📜 History data:', data.data);
        setHistoryList(data.data);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    console.log('🟢 [useEffect] Modal abierto');
    // Nota: se quitó el "document.body.style.overflow = 'hidden' / 'unset'"
    // que estaba aquí. body YA tiene overflow:hidden global (App.css) —
    // esto era redundante al abrir, y al CERRAR ponía 'unset' con estilo
    // inline, lo cual GANA por encima de la regla de la clase y dejaba el
    // body scrolleable el resto de la sesión (nadie más en la app hace
    // esto). Es la causa más probable de que el teclado virtual — que se
    // monta directamente en document.body, igual que este modal — se viera
    // "atrás" del modal: escribir un estilo inline en body fuerza un
    // recálculo de estilos que puede reordenar el pintado de los otros
    // hijos fixed de body en algunos motores de navegador. El modal ya es
    // position:fixed cubriendo toda la pantalla (VentanaListaEspera.css),
    // así que nunca necesitó bloquear el scroll del body para verse bien.

    // Cargar historial cuando se abre el modal
    fetchHistory();

    return () => {
      console.log('🔴 [useEffect] Modal cerrado');
    };
  }, []);

  // Separar reservaciones futuras de la operación del día. Antes esto
  // era una sola lista "activeList" con estados 1,2,3,4 mezclados, sin
  // distinguir fecha — una reservación para dentro de una semana se
  // veía igual que un cliente parado ahorita en el restaurante. La
  // regla de qué es "de hoy" vs "futuro" vive en un solo lugar
  // (listaEsperaHelpers.js) compartido con el banner de RevenueSelector,
  // para que ambos siempre coincidan.
  //
  // reservationsList: Reserva (status 1) cuya fecha aún no llega.
  // todayList: walk-ins (Llegó/Espera/Prioridad) + Reservas cuya fecha
  // ya llegó (hoy o antes) — se integran automáticamente sin que nadie
  // las mueva a mano.
  //
  // Mismo criterio de orden que antes: los que tienen scheduled_time
  // van primero por fecha más próxima; los que no, después, en el
  // orden en que ya venían.
  const sortBySoonest = (a, b) => {
    const aHas = !!a.scheduled_time;
    const bHas = !!b.scheduled_time;
    if (aHas && bHas) return new Date(a.scheduled_time) - new Date(b.scheduled_time);
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    return 0;
  };

  const reservationsList = waitingList
    .filter(isUpcomingReservation)
    .slice()
    .sort(sortBySoonest);

  const todayList = waitingList
    .filter(isOperationalToday)
    .slice()
    .sort(sortBySoonest);

  // Requerimiento: en cuanto un registro pasa a "Llegó" o su fecha/hora
  // programada ya venció, deja de contar como vigente/pendiente en
  // Hoy/Reservaciones (ver isOperationalToday/isUpcomingReservation ya
  // actualizados) y debe poder consultarse desde Historial.
  //
  // El Historial "oficial" viene del backend (fetchHistory, filter=history
  // — ver más abajo), pero ese endpoint no necesariamente ya conocía esta
  // regla nueva de "Llegó"/vencido. Para no depender de tocar ese PHP,
  // completamos la vista de Historial en el cliente: cualquier registro de
  // waitingList que cumpla isConcludedOrExpired y todavía no venga en el
  // historial del backend (mismo queue_id) se agrega aquí. Es solo una
  // unión para mostrar — no reemplaza ni modifica lo que ya trae el
  // backend, así que si esa lógica ya vive ahí no se duplica ni se pierde
  // nada.
  const historyIds = new Set(historyList.map((h) => h.queue_id));
  const clientSideConcluded = waitingList
    .filter((item) => isConcludedOrExpired(item) && !historyIds.has(item.queue_id))
    .slice()
    .sort(sortBySoonest);
  const displayHistoryList = [...clientSideConcluded, ...historyList];

  // Determinar qué lista mostrar según la pestaña activa
  const currentList =
    activeTab === 'today' ? todayList :
    activeTab === 'reservations' ? reservationsList :
    displayHistoryList;

  // El contenido del modal
  const modalContent = (
    <>
    <div className="rev-waiting-modal-overlay" onClick={onClose}>
      <div className="rev-waiting-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="rev-waiting-modal-header">
          <div className="rev-waiting-modal-title">
            <FaList className="rev-waiting-modal-icon" />
            <h2>
              {assignMode
                ? assignTargetName
                  ? `Asignar a ${assignTargetName}`
                  : 'Seleccionar persona'
                : 'Lista de Espera y Reservaciones'}
            </h2>
          </div>
          <button className="rev-waiting-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Pestañas. En modo asignar mesa se oculta "Reservaciones": no
            tiene sentido sentar ahora mismo a alguien cuya reservación
            todavía no llega — solo se puede asignar desde la lista
            operativa de hoy. */}
        <div className="rev-waiting-tabs">
          <button
            className={`rev-waiting-tab ${activeTab === 'today' ? 'active' : ''}`}
            onClick={() => setActiveTab('today')}
          >
            <FaUsersIcon /> Espera de Hoy ({todayList.length})
          </button>
          {!assignMode && (
            <button
              className={`rev-waiting-tab ${activeTab === 'reservations' ? 'active' : ''}`}
              onClick={() => setActiveTab('reservations')}
            >
              <FaCalendarAlt /> Reservaciones ({reservationsList.length})
            </button>
          )}
          {!assignMode && (
            <button
              className={`rev-waiting-tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('history');
                if (historyList.length === 0) fetchHistory();
              }}
            >
              <FaHistory /> Historial ({displayHistoryList.length})
            </button>
          )}
        </div>

        <div className="rev-waiting-modal-body">
          {/* Botón para agregar nuevo - En Espera de Hoy y Reservaciones */}
          {(activeTab === 'today' || activeTab === 'reservations') && !showAddForm && (
            <button
              className="rev-waiting-add-btn"
              onClick={() => {
                console.log('➕ Click en Nuevo', activeTab === 'reservations' ? 'Reservación' : 'Cliente en Espera');
                // Preseleccionar el Tipo según la pestaña desde la que se
                // agrega, para que lo más común no requiera tocar el
                // dropdown: en Reservaciones arranca en "Reserva", en
                // Espera de Hoy arranca en "Espera".
                setFormData(prev => ({
                  ...prev,
                  queue_type: activeTab === 'reservations' ? '1' : '3'
                }));
                setShowAddForm(true);
              }}
            >
              <FaPlus /> {activeTab === 'reservations' ? 'Nueva Reservación' : 'Nuevo Cliente en Espera'}
            </button>
          )}

          {/* Formulario de agregar/editar - En Espera de Hoy y Reservaciones */}
          {(activeTab === 'today' || activeTab === 'reservations') && showAddForm && (
            <div className="rev-waiting-form">
              <h3>{editingItem ? '✏️ Editar Cliente' : '➕ Nuevo Cliente'}</h3>
              
              <div className="rev-waiting-form-grid">
                <div className="rev-waiting-form-group">
                  <label><FaUser /> Nombre *</label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    placeholder="Nombre del cliente"
                    autoFocus
                  />
                </div>

                <div className="rev-waiting-form-group">
                  <label><FaPhone /> Teléfono</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Teléfono"
                  />
                </div>

                <div className="rev-waiting-form-group">
                  <label><FaUsers /> Personas</label>
                  {/* Requerimiento: no limitar a la lista fija (tope de 20) —
                      grupos grandes (banquetes) necesitan escribir el número
                      manualmente. Se cambió de <select> a input numérico
                      libre; con el teclado virtual activo, este tipo de
                      input abre el teclado en modo numérico automáticamente. */}
                  <input
                    type="number"
                    name="party_size"
                    min="1"
                    step="1"
                    value={formData.party_size}
                    onChange={handleInputChange}
                    placeholder="Número de personas"
                  />
                </div>

                <div className="rev-waiting-form-group">
                  <label><FaList /> Tipo</label>
                  <select
                    name="queue_type"
                    value={formData.queue_type}
                    onChange={handleInputChange}
                  >
                    <option value="1">📅 Reserva</option>
                    <option value="2">👋 Llegó</option>
                    <option value="3">⏳ Espera</option>
                    <option value="4">⭐ Prioridad</option>
                  </select>
                </div>

                <div className="rev-waiting-form-group">
                  <label><FaBell /> Prioridad</label>
                  <select
                    name="priority_level"
                    value={formData.priority_level}
                    onChange={handleInputChange}
                  >
                    <option value="1">🟢 Normal</option>
                    <option value="2">🟡 Media</option>
                    <option value="3">🔴 Alta</option>
                  </select>
                </div>

                {formData.queue_type === '1' && (
                  <div className="rev-waiting-form-group">
                    <label><FaCalendarAlt /> Fecha/Hora</label>
                    <input
                      type="datetime-local"
                      name="scheduled_time"
                      value={formData.scheduled_time}
                      onChange={handleInputChange}
                    />
                  </div>
                )}

                <div className="rev-waiting-form-group full-width">
                  <label>📝 Notas</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Notas adicionales (preferencias, alergias, etc.)"
                    rows="2"
                  />
                </div>
              </div>

              <div className="rev-waiting-form-actions">
                <button className="rev-waiting-form-cancel" onClick={() => {
                  console.log('❌ Click en Cancelar');
                  resetForm();
                }}>
                  Cancelar
                </button>
                <button className="rev-waiting-form-save" onClick={handleSave}>
                  <FaSave /> {editingItem ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </div>
          )}

          {/* Lista de espera */}
          {loading && activeTab === 'history' ? (
            <div className="rev-waiting-loading">
              <p>Cargando historial...</p>
            </div>
          ) : currentList.length === 0 ? (
            <div className="rev-waiting-empty">
              <p>📭 No hay {
                activeTab === 'today' ? 'personas en espera hoy' :
                activeTab === 'reservations' ? 'reservaciones próximas' :
                'registros en el historial'
              }</p>
              {(activeTab === 'today' || activeTab === 'reservations') && (
                <small>Haz clic en "{activeTab === 'reservations' ? 'Nueva Reservación' : 'Nuevo Cliente en Espera'}" para agregar</small>
              )}
            </div>
          ) : (
            <div className="rev-waiting-list">
              {currentList.map((item, index) => {
                const statusInfo = getStatusInfo(item.status);
                const isExpanded = expandedItems[item.queue_id];
                const isActive = [1, 2, 3, 4].includes(item.status);
                
                return (
                  <div key={item.queue_id || index} className="rev-waiting-item">
                    <div className="rev-waiting-item-main">
                      <div className="rev-waiting-item-position">
                        <span className="rev-waiting-position-number">#{index + 1}</span>
                        <span className="rev-waiting-party-size-large">
                          {item.party_size || 1}
                        </span>
                      </div>
                      
                      <div className="rev-waiting-item-info">
                        <div className="rev-waiting-item-header">
                          <span className="rev-waiting-item-name">
                            {item.customer_name || 'Cliente'}
                          </span>
                          <span className="rev-waiting-item-time-small">
                            <FaClock /> {formatMinutes(item.minutos_espera)}
                          </span>
                        </div>
                        
                        <div className="rev-waiting-item-badges">
                          <span 
                            className="rev-waiting-status-badge"
                            style={{ backgroundColor: statusInfo.color }}
                          >
                            {statusInfo.icon} {statusInfo.name}
                          </span>
                          
                          {item.phone && (
                            <span className="rev-waiting-phone-badge">
                              <FaPhone size={10} /> {item.phone}
                            </span>
                          )}
                          
                          {item.priority_level > 1 && isActive && (
                            <span className="rev-waiting-priority-badge" style={{
                              backgroundColor: item.priority_level === 3 ? '#ef4444' : '#f59e0b'
                            }}>
                              {item.priority_level === 3 ? '🔴 Alta' : '🟡 Media'}
                            </span>
                          )}
                        </div>

                        {item.scheduled_time && !isExpanded && (
                          <div className="rev-waiting-scheduled-preview">
                            <FaCalendarAlt size={10} /> Reservado para: {new Date(item.scheduled_time).toLocaleString()}
                          </div>
                        )}

                        {item.notes && !isExpanded && (
                          <div className="rev-waiting-notes-preview">
                            📝 {item.notes}
                          </div>
                        )}
                      </div>
                      
                      <div className="rev-waiting-item-actions-compact">
                        {assignMode && isActive ? (
                          <button
                            className="rev-waiting-expanded-assign-btn"
                            style={{ fontSize: '0.8rem', padding: '6px 10px' }}
                            onClick={() => handleAssignToTable(item)}
                            title="Asignar a esta mesa"
                          >
                            <FaChair /> Asignar
                          </button>
                        ) : (
                          <>
                            <button
                              className="rev-waiting-item-expand"
                              onClick={() => handleExpandClick(item.queue_id)}
                              title={isExpanded ? "Ver menos" : "Ver más"}
                            >
                              {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            </button>

                            {isActive && (
                              <button
                                className="rev-waiting-item-edit-small"
                                onClick={() => handleEdit(item)}
                                title="Editar cliente"
                              >
                                <FaEdit />
                              </button>
                            )}

                            {/* Requerimiento "WhatsApp": acceso directo desde
                                la lista, sin necesidad de expandir el item. */}
                            <button
                              className="rev-waiting-whatsapp-btn-small"
                              onClick={(e) => { e.stopPropagation(); setWhatsappItem(item); }}
                              title="Enviar WhatsApp"
                            >
                              <FaWhatsapp />
                            </button>

                          </>
                        )}
                      </div>
                    </div>

                    {/* Contenido expandido */}
                    {isExpanded && (
                      <div className="rev-waiting-item-expanded">
                        <div className="rev-waiting-expanded-details">
                          <div className="rev-waiting-expanded-row">
                            <FaClock /> Hora de registro: {item.hora_registro || '--:--'}
                          </div>
                          <div className="rev-waiting-expanded-row">
                            <FaHourglassHalf /> Tiempo de espera: {formatMinutes(item.minutos_espera)}
                          </div>
                          {item.phone && (
                            <div className="rev-waiting-expanded-row">
                              <FaPhone /> Teléfono: {item.phone}
                            </div>
                          )}
                          {item.scheduled_time && (
                            <div className="rev-waiting-expanded-row">
                              <FaCalendarAlt /> Reservado para: {new Date(item.scheduled_time).toLocaleString()}
                            </div>
                          )}
                          {item.notes && (
                            <div className="rev-waiting-expanded-notes">
                              <strong>📝 Notas:</strong> {item.notes}
                            </div>
                          )}
                        </div>
                        
                        {isActive && (
                          <div className="rev-waiting-expanded-actions">
                            <button
                              className="rev-waiting-expanded-status-btn"
                              onClick={() => handleStatusMenuClick(item.queue_id)}
                            >
                              {showStatusMenu === item.queue_id ? '▼ Cerrar' : '► Cambiar Estado'}
                            </button>
                            <button
                              className="rev-waiting-expanded-assign-btn"
                              onClick={() => {
                                if (canAssignToTable && onAssign) {
                                  // Modo pick: cambia estado a Sentado (5) y deja al usuario elegir la mesa
                                  handleStatusChange(item, 5);
                                  onAssign(item);
                                  onClose();
                                } else {
                                  handleAssignToTable(item);
                                }
                              }}
                            >
                              <FaChair /> Asignar Mesa
                            </button>
                            <button
                              className="rev-waiting-whatsapp-btn"
                              onClick={() => setWhatsappItem(item)}
                            >
                              <FaWhatsapp /> Enviar WhatsApp
                            </button>
                          </div>
                        )}

                        {/* Menú de estados - Solo para activos */}
                        {isActive && showStatusMenu === item.queue_id && (
                          <div className="rev-waiting-status-menu-mobile">
                            {statusMenuCatalog.map(status => (
                              <button
                                key={status.id}
                                className="rev-waiting-status-option-mobile"
                                style={{ borderLeft: `4px solid ${status.color}` }}
                                onClick={() => handleStatusOptionClick(item, status)}
                              >
                                {status.icon} {status.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rev-waiting-modal-footer">
          <div className="rev-waiting-stats">
            {activeTab === 'today' ? (
              <>
                <span>📋 Total hoy: {todayList.length}</span>
                <span>📅 Reserva: {todayList.filter(w => w.status === 1).length}</span>
                <span>⏳ Espera: {todayList.filter(w => w.status === 3).length}</span>
                <span>⭐ Prioridad: {todayList.filter(w => w.status === 4).length}</span>
              </>
            ) : activeTab === 'reservations' ? (
              <span>📅 Próximas reservaciones: {reservationsList.length}</span>
            ) : (
              <span>📋 Historial: {displayHistoryList.length} registros</span>
            )}
          </div>
          <button className="rev-waiting-modal-btn" onClick={() => {
            console.log('❌ Click en Cerrar');
            onClose();
          }}>
            Cerrar
          </button>
        </div>
      </div>
    </div>

    {/* Requerimiento "WhatsApp para Reservaciones y Lista de Espera" —
        modal independiente, no interfiere con el resto del flujo de
        VentanaListaEspera (edición, cambio de estado, asignación, etc.). */}
    {whatsappItem && (
      <ReservationWhatsAppModal
        item={whatsappItem}
        property={property}
        onClose={() => setWhatsappItem(null)}
      />
    )}
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

VentanaListaEspera.propTypes = {
  waitingList: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func,
  onAssign: PropTypes.func,
  revenues: PropTypes.array
};

export default VentanaListaEspera;